import React from 'react';
import { HiOutlineReceiptTax, HiOutlineSave } from 'react-icons/hi';
import { LuBanknote, LuChartColumnIncreasing } from 'react-icons/lu';
import { MdOutlinePending } from 'react-icons/md';

interface DashboardFinancialRowProps {
  paymentReceived: number;
  billed: number;
  due: number;
  inPipeline: number;
  loading?: boolean;
  onCardClick?: (cardKey: 'PAYMENT_RECEIVED' | 'BILLED' | 'DUE' | 'IN_PIPELINE') => void;
  clickableCardKeys?: Array<'PAYMENT_RECEIVED' | 'BILLED' | 'DUE' | 'IN_PIPELINE'>;
}

type FinancialCardKey = 'PAYMENT_RECEIVED' | 'BILLED' | 'DUE' | 'IN_PIPELINE';

interface FinancialCardConfig {
  key: FinancialCardKey;
  label: string;
  icon: React.ReactNode;
  bgGradient: string;
  borderColor: string;
  titleColor: string;
  valueColor: string;
  amount: number;
}

const DashboardFinancialRow: React.FC<DashboardFinancialRowProps> = ({
  paymentReceived,
  billed,
  due,
  inPipeline,
  loading = false,
  onCardClick,
  clickableCardKeys = [],
}) => {
  // Format currency
  const formatCurrency = (amount: number) => {
    const safeAmount = isNaN(amount) || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(safeAmount)
      .replace('₹', '₹');
  };

  // Define the four financial cards configuration
  const financialCards: FinancialCardConfig[] = [
    {
      key: 'PAYMENT_RECEIVED',
      label: 'PAYMENT RECEIVED',
      icon: <LuBanknote className='w-full h-full' />,
      bgGradient: 'bg-gradient-to-b from-white to-[#f0fff3]',
      borderColor: 'border-[#6ed485]',
      titleColor: 'text-[#6dd485]',
      valueColor: 'text-[#28a745]',
      amount: paymentReceived,
    },
    {
      key: 'BILLED',
      label: 'BILLED',
      icon: <HiOutlineReceiptTax className='w-full h-full' />,
      bgGradient: 'bg-gradient-to-b from-white to-[#f0f8ff]',
      borderColor: 'border-[#4a90d9]',
      titleColor: 'text-[#4a90d9]',
      valueColor: 'text-[#2563eb]',
      amount: billed,
    },
    {
      key: 'DUE',
      label: 'DUE',
      icon: <MdOutlinePending className='w-full h-full' />,
      bgGradient: 'bg-gradient-to-b from-white to-[#fff8f0]',
      borderColor: 'border-[#f59e0b]',
      titleColor: 'text-[#f59e0b]',
      valueColor: 'text-[#d97706]',
      amount: due,
    },
    {
      key: 'IN_PIPELINE',
      label: 'IN-PIPELINE',
      icon: <HiOutlineSave className='w-full h-full' />,
      bgGradient: 'bg-gradient-to-b from-white to-[#f7f7f7]',
      borderColor: 'border-[#20b2aa]',
      titleColor: 'text-[#2c2f61]',
      valueColor: 'text-[#2c2f61]',
      amount: inPipeline,
    },
  ];


  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-emerald-600 rounded-full"></div>
          <h2 className="text-xl font-bold text-gray-800">
            Financial <span className="text-gray-400">Overview</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm animate-pulse"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="h-5 w-24 bg-gray-200 rounded"></div>
              </div>
              <div className="h-12 w-32 bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-28 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      {/* Heading */}
      <div className="flex items-center gap-4 mb-6">
        <div className='w-8 h-8'><LuChartColumnIncreasing className='w-full h-full' /></div>
        <h2 className="font-semibold text-[#333333] text-2xl tracking-[-0.64px]">
          Projects Finance
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {financialCards.map(card => {
          const isClickable = Boolean(onCardClick) && clickableCardKeys.includes(card.key);

          return (
            <div
              key={card.key}
              className={`${card.bgGradient} ${card.borderColor} rounded-2xl border overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-1 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={isClickable ? () => onCardClick?.(card.key) : undefined}
              onKeyDown={
                isClickable
                  ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onCardClick?.(card.key);
                      }
                    }
                  : undefined
              }
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
            >
              <div className="p-6">
                {/* Icon and Label */}
                <div className="flex items-center gap-2 mb-2 min-w-0">
                  <div className={`w-6 h-6 flex-shrink-0 ${card.titleColor}`}>
                    {card.icon}
                  </div>
                  <h3 className={`font-semibold ${card.titleColor} text-lg sm:text-xl tracking-[-0.48px] truncate`}>
                    {card.label}
                  </h3>
                </div>

                {/* Amount */}
                <p className={`font-extrabold ${card.valueColor} text-4xl tracking-[-0.72px]`}>
                  {formatCurrency(card.amount)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DashboardFinancialRow;
